import {
  InstallmentWithPlanItemsDetails,
  PaymentInstallmentStatus,
} from '../../../../../../../api/types/payments/installments/entities';
import { InstallmentBankTransferWithPlanItemsDetails } from '../../../../../../../api/types/students/user-details-student';
import { InstallmentPlanSummary } from '../componenets/installment-summary';

export interface CalculateInstallmentSummariesParams {
  allocationId: number;
  creditCardInstallmentPlans: InstallmentWithPlanItemsDetails[];
  bankTransferInstallmentPlans: InstallmentBankTransferWithPlanItemsDetails[];
}

/**
 * Calculates installment plan summaries for a specific allocation.
 * Processes both credit card and bank transfer installment plans.
 */
export const calculateInstallmentSummaries = ({
  allocationId,
  creditCardInstallmentPlans,
  bankTransferInstallmentPlans,
}: CalculateInstallmentSummariesParams): InstallmentPlanSummary[] => {
  const summaries: InstallmentPlanSummary[] = [];

  // Process credit card installment plans
  creditCardInstallmentPlans.forEach((plan) => {
    const allocationItem = plan.paymentInstallmentPlanItemAllocations.find(
      (item) => item.userAllocationId === allocationId,
    );
    if (allocationItem) {
      const paidCount = plan.installment.filter(
        (i) => i.status === PaymentInstallmentStatus.PAID,
      ).length;
      const pendingCount = plan.installment.filter(
        (i) => i.status === PaymentInstallmentStatus.PENDING,
      ).length;
      const failedCount = plan.installment.filter(
        (i) => i.status === PaymentInstallmentStatus.FAILED,
      ).length;
      const nextPayment = plan.installment
        .filter((i) => i.status === PaymentInstallmentStatus.PENDING)
        .sort(
          (a, b) =>
            new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime(),
        )[0];

      summaries.push({
        planId: plan.installmentPlan.id,
        type: 'credit_card',
        totalAmount: plan.installmentPlan.totalAmount,
        allocationAmount: allocationItem.allocationAmount,
        numberOfInstallments: plan.installmentPlan.numberOfInstallments,
        paidCount,
        pendingCount,
        failedCount,
        nextPaymentDate: nextPayment ? new Date(nextPayment.dueDate) : null,
        createdAt: new Date(plan.installmentPlan.createdAt),
      });
    }
  });

  // Process bank transfer installment plans
  bankTransferInstallmentPlans.forEach((plan) => {
    const allocationItem = plan.paymentInstallmentPlanItemAllocations.find(
      (item) => item.userAllocationId === allocationId,
    );
    if (allocationItem) {
      const paidCount = plan.installment.filter(
        (i) => i.status === PaymentInstallmentStatus.PAID,
      ).length;
      const pendingCount = plan.installment.filter(
        (i) => i.status === PaymentInstallmentStatus.PENDING,
      ).length;
      const failedCount = plan.installment.filter(
        (i) => i.status === PaymentInstallmentStatus.FAILED,
      ).length;
      const nextPayment = plan.installment
        .filter((i) => i.status === PaymentInstallmentStatus.PENDING)
        .sort(
          (a, b) =>
            new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime(),
        )[0];

      summaries.push({
        planId: plan.installmentPlan.id,
        type: 'bank_transfer',
        totalAmount: plan.installmentPlan.totalAmount,
        allocationAmount: allocationItem.allocationAmount,
        numberOfInstallments: plan.installmentPlan.numberOfInstallments,
        paidCount,
        pendingCount,
        failedCount,
        nextPaymentDate: nextPayment ? new Date(nextPayment.dueDate) : null,
        createdAt: new Date(plan.installmentPlan.createdAt),
      });
    }
  });

  return summaries;
};

/**
 * Calculates the total installment amount from a list of summaries.
 */
export const calculateTotalInstallmentAmount = (
  summaries: InstallmentPlanSummary[],
): number => {
  return summaries.reduce((sum, plan) => sum + plan.allocationAmount, 0);
};
